import { useEffect, useMemo, useState } from "react";
import api, { euro } from "../lib/api";
import { toast } from "sonner";
import { Percent, ArrowUpDown, TrendingUp, TrendingDown, Download, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const SORTS = {
  margin_pct: (a, b) => (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity),
  margin_abs: (a, b) => (b.margin_abs ?? -Infinity) - (a.margin_abs ?? -Infinity),
  name: (a, b) => a.name.localeCompare(b.name),
  sale_price: (a, b) => (b.sale_price ?? -Infinity) - (a.sale_price ?? -Infinity),
};

export default function Marginalita() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("margin_pct");
  const [colors, setColors] = useState({});
  const [filterCat, setFilterCat] = useState("");
  const [threshold, setThreshold] = useState(20);
  const [riskFirst, setRiskFirst] = useState(false);
  const [vatFilter, setVatFilter] = useState("");

  useEffect(() => {
    api.get("/catalog/margins").then(({ data }) => setData(data)).catch(() => {});
    api.get("/categories/colors").then(({ data }) => setColors(data)).catch(() => {});
    api.get("/settings").then(({ data }) => { if (data?.margin_threshold_pct != null) setThreshold(data.margin_threshold_pct); }).catch(() => {});
  }, []);

  const saveThreshold = async (v) => { setThreshold(v); await api.put("/settings", { margin_threshold_pct: Number(v) || 0 }).catch(() => {}); };
  const download = async (path, filename) => {
    try { const { data } = await api.get(path, { responseType: "blob" }); const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error("Errore nel download del report"); }
  };
  const lowCount = (data?.products || []).filter((p) => p.margin_pct != null && p.margin_pct < threshold).length;
  const chartData = useMemo(() => {
    const g = {};
    (data?.products || []).forEach((p) => { if (p.margin_pct != null) { (g[p.category] = g[p.category] || []).push(p.margin_pct); } });
    return Object.entries(g).map(([name, arr]) => ({ name, margin: Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 })).sort((a, b) => b.margin - a.margin);
  }, [data]);

  const catsPresent = Array.from(new Set((data?.products || []).map((p) => p.category).filter(Boolean)));
  const products = useMemo(() => {
    let list = [...(data?.products || [])].filter((p) => !filterCat || p.category === filterCat).sort(SORTS[sort]);
    if (riskFirst) {
      const isLow = (p) => p.margin_pct != null && p.margin_pct < threshold;
      const low = list.filter(isLow).sort((a, b) => a.margin_pct - b.margin_pct);
      const rest = list.filter((p) => !isLow(p));
      list = [...low, ...rest];
    }
    return list;
  }, [data, sort, filterCat, riskFirst, threshold]);
  const best = products.find((p) => p.margin_pct != null);
  const worst = [...products].reverse().find((p) => p.margin_pct != null);

  const Th = ({ id, children, right }) => (
    <th className={`py-4 px-6 font-bold ${right ? "text-right" : "text-left"}`}>
      <button onClick={() => setSort(id)} className={`inline-flex items-center gap-1 hover:text-brand ${sort === id ? "text-brand" : ""}`} data-testid={`sort-${id}`}>
        {children} <ArrowUpDown size={12} />
      </button>
    </th>
  );

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Dashboard</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Marginalità</h1>
          <p className="text-sm text-stone-500 mt-2">Prodotti vendibili con miglior prezzo d'acquisto, prezzo di vendita e margine. Clic sulle intestazioni per ordinare.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Soglia margine</label>
            <input data-testid="margin-threshold" type="number" min="0" max="100" value={threshold} onChange={(e) => saveThreshold(e.target.value)}
              className="w-20 border border-stone-300 bg-white px-2 py-1.5 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
            <span className="text-sm text-stone-400">%</span>
          </div>
          <button data-testid="risk-sort-toggle" onClick={() => setRiskFirst((v) => !v)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${riskFirst ? "bg-alert text-white border-alert" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            <AlertTriangle size={15} /> {riskFirst ? "Rischio in cima ✓" : "Prima i più critici"}
          </button>
          <select data-testid="export-vat-filter" value={vatFilter} onChange={(e) => setVatFilter(e.target.value)}
            className="border border-stone-300 bg-white px-3 py-2 text-sm rounded-full outline-none focus:ring-2 focus:ring-brand/40 text-stone-600">
            <option value="">Export: tutte IVA</option>
            {["22", "10", "5", "4", "0 esente", "0 fuori campo"].map((v) => <option key={v} value={v}>{/^\d+$/.test(v) ? `IVA ${v}%` : `IVA ${v}`}</option>)}
          </select>
          <button data-testid="export-margins-xlsx" onClick={() => download(`/reports/margins.xlsx${vatFilter ? `?vat=${encodeURIComponent(vatFilter)}` : ""}`, "margine_reparti.xlsx")}
            className="flex items-center gap-2 rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-white transition-colors"><Download size={15} /> Excel</button>
          <button data-testid="export-margins-pdf" onClick={() => download(`/reports/margins.pdf${vatFilter ? `?vat=${encodeURIComponent(vatFilter)}` : ""}`, "margine_reparti.pdf")}
            className="flex items-center gap-2 rounded-full bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-hover transition-colors"><Download size={15} /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="stat-count" className="bg-white border border-stone-200 rounded-sm p-6">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Prodotti vendibili</p>
          <p className="font-serif text-4xl font-light text-stone-900 mt-2">{data?.count ?? "—"}</p>
        </div>
        <div data-testid="stat-avg" className="bg-brand text-white rounded-sm p-6 noise-overlay">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-white/70 flex items-center gap-1"><Percent size={13} /> Margine medio netto</p>
          <p className="font-serif text-4xl font-light mt-2">{data?.avg_margin_pct != null ? `${data.avg_margin_pct}%` : "—"}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-brand font-semibold"><TrendingUp size={13} /> Migliore</span>
            <span className="text-stone-700">{best ? `${best.name} · ${best.margin_pct}%` : "—"}</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-3">
            <span className="flex items-center gap-1 text-alert font-semibold"><TrendingDown size={13} /> Peggiore</span>
            <span className="text-stone-700">{worst && worst !== best ? `${worst.name} · ${worst.margin_pct}%` : "—"}</span>
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div data-testid="margin-chart" className="bg-white border border-stone-200 rounded-sm p-6">
          <h3 className="font-serif text-2xl text-stone-900 mb-4">Margine medio per reparto</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#78716C" }} />
              <YAxis tick={{ fontSize: 12, fill: "#78716C" }} unit="%" />
              <Tooltip formatter={(v) => `${v}%`} cursor={{ fill: "#F5F5F4" }} />
              <Bar dataKey="margin" radius={[4, 4, 0, 0]}>
                {chartData.map((c) => <Cell key={c.name} fill={colors[c.name] || "#2C4A3B"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {lowCount > 0 && (
        <div data-testid="low-margin-banner" className="bg-alert/10 border border-alert/30 rounded-sm p-4 flex items-center gap-3">
          <AlertTriangle size={18} className="text-alert shrink-0" />
          <p className="text-sm text-stone-700"><b className="text-alert">{lowCount} prodotti</b> hanno un margine sotto la soglia del {threshold}%. Rivedi prezzi di vendita o fornitori.</p>
        </div>
      )}

      <div data-testid="reparto-filter" className="flex flex-wrap gap-2">
        <button data-testid="filter-all" onClick={() => setFilterCat("")} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!filterCat ? "bg-brand text-white border-brand" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>Tutti</button>
        {catsPresent.map((c) => (
          <button key={c} data-testid={`filter-${c}`} onClick={() => setFilterCat(filterCat === c ? "" : c)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${filterCat === c ? "text-white border-transparent" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}
            style={filterCat === c ? { background: colors[c] || "#2C4A3B" } : undefined}>
            <span className="w-2 h-2 rounded-full" style={{ background: colors[c] || "#A8A29E" }} />{c}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
            <Th id="name">Prodotto</Th>
            <th className="text-left py-4 px-6 font-bold">Categoria</th>
            <th className="text-right py-4 px-6 font-bold">Prezzo unit. reale</th>
            <th className="text-right py-4 px-6 font-bold">Costo porz.</th>
            <Th id="sale_price" right>Prezzo vendita</Th>
            <Th id="margin_abs" right>Margine netto €</Th>
            <Th id="margin_pct" right>Margine netto %</Th>
            <th className="text-right py-4 px-6 font-bold">Margine lordo %</th>
            <th className="text-right py-4 px-6 font-bold">Margine porz.</th>
          </tr></thead>
          <tbody>
            {products.length === 0 && <tr><td colSpan={9} className="py-16 text-center text-stone-400"><Percent size={28} className="mx-auto mb-2" /> Nessun prodotto vendibile. Imposta un prezzo di vendita in Anagrafica o importa le vendite.</td></tr>}
            {products.map((p) => (
              <tr key={p.id} data-testid={`margin-row-${p.id}`} className={`border-b border-stone-100 hover:bg-stone-50 ${p.margin_pct != null && p.margin_pct < threshold ? "bg-alert/5" : ""}`}>
                <td className="py-4 px-6 font-semibold text-stone-800">{p.name}</td>
                <td className="py-4 px-6 text-stone-600"><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[p.category] || "#A8A29E" }} />{p.category || "—"}</span></td>
                <td className="py-4 px-6 text-right text-stone-700">{p.best_purchase != null ? <>{euro(p.best_purchase)}{p.best_unit_measure && p.best_unit_measure !== "pezzo" ? <span className="text-xs text-stone-400">/{p.best_unit_measure}</span> : ""}</> : "—"}</td>
                <td data-testid={`portion-cost-${p.id}`} className="py-4 px-6 text-right text-stone-700">{p.portion_cost != null ? euro(p.portion_cost) : "—"}</td>
                <td className="py-4 px-6 text-right text-stone-700">{p.sale_price != null ? <>{euro(p.sale_price)}{p.sale_price_net != null && p.sale_price_net !== p.sale_price ? <span className="block text-[10px] text-stone-400">netto {euro(p.sale_price_net)}</span> : null}</> : "—"}</td>
                <td className="py-4 px-6 text-right text-stone-700">{p.margin_abs != null ? euro(p.margin_abs) : "—"}</td>
                <td className="py-4 px-6 text-right">
                  {p.margin_pct == null ? <span className="text-xs text-stone-400">—</span>
                    : <span className={`text-xs font-bold px-2 py-1 rounded-full ${p.margin_pct < threshold ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{p.margin_pct}%</span>}
                </td>
                <td data-testid={`margin-gross-${p.id}`} className="py-4 px-6 text-right text-stone-400 text-xs">{p.margin_pct_gross != null ? `${p.margin_pct_gross}%` : "—"}</td>
                <td data-testid={`portion-margin-${p.id}`} className="py-4 px-6 text-right">
                  {p.portion_margin_pct == null ? <span className="text-xs text-stone-400">—</span>
                    : <span className={`text-xs font-bold px-2 py-1 rounded-full ${p.portion_margin_pct < threshold ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{p.portion_margin_pct}%</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
